import { acceptHMRUpdate, defineStore } from "pinia";
import { useNetCoords } from "./netCoordsModifier";
import type { CanvasDimensions } from "./CanvasDimensions";
import type { Uid } from "@/helpers/utility/Id";

export type NetDisplayMode = "hidden" | "value" | "impacts";

export interface NetState {
  translateX: number;
  translateY: number;
  scale: number;
  maxScale: number;
  minScale: number;

  itemsOrder: Uid<string>[];

  canvasDimensions: CanvasDimensions;
  sidebarClosed?: boolean;
}

export const useNetStore = defineStore("model/net", {
  state: (): NetState => ({
    translateX: 0,
    translateY: 0,
    scale: 0.6,
    maxScale: 2,
    minScale: 0.1,

    itemsOrder: [],

    canvasDimensions: {
      scale: 1,
      x: 0,
      y: 0,
      width: 1000,
      height: 700,
      left: 0,
      top: 0,
      right: 1000,
      bottom: 700,

      translateX: 0,
      translateY: 0,

      containerX: 0,
      containerY: 0,
      containerWidth: 0,
      containerHeight: 0
    },

    sidebarClosed: false
  }),
  getters: {
    viewportBounds() {
      const canvasDimensions = this.canvasDimensions;
      const { toLocalRect } = useNetCoords({ canvasDimensions });

      const topLeft = {
        x: canvasDimensions.containerX,
        y: canvasDimensions.containerY
      };
      const bottomRight = {
        x: topLeft.x + canvasDimensions.containerWidth,
        y: topLeft.y + canvasDimensions.containerHeight
      };
      const bounds = toLocalRect({
        left: topLeft.x,
        top: topLeft.y,
        right: bottomRight.x,
        bottom: bottomRight.y
      });

      return bounds;
    }
  },
  actions: {
    set(props: Partial<NetState>) {
      this.$patch((state) => {
        Object.assign(state, props);
      });
    },
    updateKey<N extends keyof NetState>(name: N, value: NetState[N]) {
      this.$patch((state) => {
        state[name] = value;
      });
    },
    reset() {
      this.$reset();
    },

    moveToFront(uid: Uid<string>) {
      const index = this.itemsOrder.indexOf(uid);
      if (index == -1) {
        return;
      }
      this.itemsOrder.splice(index, 1);
      this.itemsOrder.push(uid);
    },
    moveForward(uid: Uid<string>) {
      const index = this.itemsOrder.indexOf(uid);
      if (index == -1) {
        return;
      }
      if (index == this.itemsOrder.length - 1) {
        return;
      }
      this.itemsOrder.splice(index, 1);
      this.itemsOrder.splice(index + 1, 0, uid);
    },
    moveBackward(uid: Uid<string>) {
      const index = this.itemsOrder.indexOf(uid);
      if (index == -1) {
        return;
      }
      if (index == 0) {
        return;
      }
      this.itemsOrder.splice(index, 1);
      this.itemsOrder.splice(index - 1, 0, uid);
    },
    moveToBack(uid: Uid<string>) {
      const index = this.itemsOrder.indexOf(uid);
      if (index == -1) {
        return;
      }
      this.itemsOrder.splice(index, 1);
      this.itemsOrder.unshift(uid);
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNetStore, import.meta.hot));
}
